import { Link, useNavigate } from 'react-router-dom'
import { ArrowDown, ArrowUp, Paperclip, TriangleAlert, UserRound } from 'lucide-react'
import {
  Badge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui'
import { useT } from '@/i18n/I18nProvider'
import { cn, formatDate, formatDateTime, formatRelative } from '@/lib/utils'
import { activeSort, type SortColumn } from '@/lib/submissionQueue'
import type { QueueState } from '@/lib/submissionQueue'
import type { Submission } from '@/types/api'

/**
 * The deadline, red when it has passed.
 *
 * The colour is backed by an icon and, for a screen reader, the word — a red
 * date on its own is invisible to one man in twelve.
 */
function Due({ submission }: { submission: Submission }) {
  const t = useT()

  if (!submission.dueAt) return <span className="muted">{t('submissions.card.noDue')}</span>

  return submission.isOverdue ? (
    <span className="inline-flex items-center gap-1 font-medium text-rose-600 dark:text-rose-400">
      <TriangleAlert className="size-3.5 shrink-0" aria-hidden />
      {formatDate(submission.dueAt)}
      <span className="sr-only"> — {t('submissions.overdue')}</span>
    </span>
  ) : (
    <span>{formatDate(submission.dueAt)}</span>
  )
}

/**
 * The desk version: one line per report, every column sortable.
 *
 * The whole row opens the report, for the mouse; the title inside it is the
 * real link, for the keyboard, a screen reader and a middle click. Sorting is
 * the server's — the list is paginated, so a sort done here would only order
 * the twenty rows on screen.
 */
export function SubmissionTable({
  rows,
  state,
  onSort,
  showPlant,
}: {
  rows: Submission[]
  state: QueueState
  onSort: (column: SortColumn) => void
  /** Only when the reader sees more than one plant; otherwise it is the same word on every row. */
  showPlant: boolean
}) {
  const t = useT()
  const navigate = useNavigate()
  const sorted = activeSort(state)

  const header = (column: SortColumn | null, label: string, className?: string) => {
    const active = column !== null && sorted.column === column

    return (
      <TableHead
        className={className}
        aria-sort={active ? (sorted.direction === 'asc' ? 'ascending' : 'descending') : undefined}
      >
        {column ? (
          <button
            type="button"
            onClick={() => onSort(column)}
            aria-label={t('submissions.col.sortBy', { column: label })}
            className={cn(
              'inline-flex items-center gap-1 rounded-sm hover:text-foreground focus-visible:ring-ring/50 focus-visible:ring-3 focus-visible:outline-none',
              active ? 'text-foreground font-semibold' : 'text-muted-foreground',
            )}
          >
            {label}
            {active &&
              (sorted.direction === 'asc' ? (
                <ArrowUp className="size-3" aria-hidden />
              ) : (
                <ArrowDown className="size-3" aria-hidden />
              ))}
          </button>
        ) : (
          <span className="text-muted-foreground">{label}</span>
        )}
      </TableHead>
    )
  }

  return (
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          {header(null, t('submissions.col.reference'))}
          {header('title', t('submissions.col.title'))}
          {header('workCenter', showPlant ? t('submissions.col.machine') : t('submissions.filter.workCenter'))}
          {header('type', t('submissions.col.type'))}
          {header('priority', t('submissions.col.priority'))}
          {header('status', t('submissions.col.status'))}
          {header('due', t('submissions.col.due'))}
          {header('assignee', t('submissions.col.assignee'))}
          {header('created', t('submissions.col.created'), 'text-right')}
        </TableRow>
      </TableHeader>

      <TableBody>
        {rows.map((submission) => (
          <TableRow
            key={submission.id}
            onClick={(event) => {
              // Let a click on the link itself, or a modified click, do what
              // links do.
              if (event.defaultPrevented || (event.target as HTMLElement).closest('a')) return
              navigate(`/prijave/${submission.id}`)
            }}
            className="cursor-pointer"
          >
            <TableCell className="muted text-xs tabular-nums">{submission.referenceNo}</TableCell>
            <TableCell className="max-w-72">
              <Link
                to={`/prijave/${submission.id}`}
                className="block truncate font-medium hover:underline focus-visible:underline focus-visible:outline-none"
              >
                {submission.title || submission.typeLabel}
              </Link>
              {(submission.attachmentCount ?? 0) > 0 && (
                <span className="muted inline-flex items-center gap-0.5 text-xs">
                  <Paperclip className="size-3" aria-hidden />
                  {submission.attachmentCount}
                </span>
              )}
            </TableCell>
            <TableCell className="max-w-56">
              <span className="block truncate">{submission.workCenter?.name}</span>
              {showPlant && submission.workCenter?.plant && (
                <span className="muted block truncate text-xs">{submission.workCenter.plant.name}</span>
              )}
            </TableCell>
            <TableCell>
              <Badge color={submission.typeColor}>{submission.typeLabel}</Badge>
            </TableCell>
            <TableCell>
              <Badge color={submission.priorityColor}>{submission.priorityLabel}</Badge>
            </TableCell>
            <TableCell>
              <Badge color={submission.statusColor}>{submission.statusLabel}</Badge>
            </TableCell>
            <TableCell className="tabular-nums">
              <Due submission={submission} />
            </TableCell>
            <TableCell className="max-w-40">
              {submission.assignedTo ? (
                <span className="block truncate">{submission.assignedTo.name}</span>
              ) : (
                <span className="muted">—</span>
              )}
            </TableCell>
            <TableCell
              className="muted text-right text-xs"
              title={formatDateTime(submission.createdAt)}
            >
              {formatRelative(submission.createdAt)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}

/**
 * The phone version: a card per report, with what a person on the floor
 * checks first — where it stands, by when, and who has it.
 */
export function SubmissionCards({ rows, showPlant }: { rows: Submission[]; showPlant: boolean }) {
  const t = useT()

  return (
    <ul className="space-y-2">
      {rows.map((submission) => (
        <li key={submission.id}>
          <Link
            to={`/prijave/${submission.id}`}
            className="card p-card block transition-colors hover:bg-[var(--surface-sunken)] focus-visible:ring-ring/50 focus-visible:ring-3 focus-visible:outline-none"
          >
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate font-medium">{submission.title || submission.typeLabel}</p>
                <p className="muted mt-0.5 truncate text-xs">
                  {submission.referenceNo} · {submission.workCenter?.name}
                  {showPlant && submission.workCenter?.plant && ` · ${submission.workCenter.plant.name}`}
                </p>
              </div>
              <Badge color={submission.statusColor} className="shrink-0">
                {submission.statusLabel}
              </Badge>
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <Badge color={submission.typeColor}>{submission.typeLabel}</Badge>
              <Badge color={submission.priorityColor}>{submission.priorityLabel}</Badge>
              {(submission.attachmentCount ?? 0) > 0 && (
                <span className="muted inline-flex items-center gap-0.5 text-xs">
                  <Paperclip className="size-3.5" aria-hidden />
                  {submission.attachmentCount}
                </span>
              )}
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
              {submission.dueAt ? (
                <span className={cn(!submission.isOverdue && 'muted')}>
                  {submission.isOverdue ? (
                    <Due submission={submission} />
                  ) : (
                    t('submissions.card.due', { date: formatDate(submission.dueAt) })
                  )}
                </span>
              ) : null}
              <span className="muted inline-flex min-w-0 items-center gap-1">
                <UserRound className="size-3.5 shrink-0" aria-hidden />
                <span className="truncate">
                  {submission.assignedTo?.name ?? t('submissions.card.nobody')}
                </span>
              </span>
              <span className="muted ml-auto">{formatRelative(submission.createdAt)}</span>
            </div>
          </Link>
        </li>
      ))}
    </ul>
  )
}
